import React, { useEffect, useState } from "react";
import StructuralBackButton from "../shared/StructuralBackButton";
import { Avatar } from "../shared/Avatar";
import { _apiGet, _apiPut } from "../../lib/apiClient";
import { useAppSettings } from "../../lib/AppSettingsContext";

// =============================================================
// PastorsDirectoryPage (/admin/pastors-directory) — Super Admin
// CRUD for the public Pastors Directory shown on the booking page.
// Now also manages each pastor's appointment-type availability
// (On-the-Fly + Weekday) and "Away" status / message.
// =============================================================

const SHORT_DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const LONG_DAYS  = ["mon", "tue", "wed", "thu", "fri"];
const DAY_LABEL = { sun: "SUN", mon: "MON", tue: "TUE", wed: "WED", thu: "THU", fri: "FRI", sat: "SAT" };

export default function PastorsDirectoryPage() {
  const { settings: appSettings } = useAppSettings();
  const apptShortName = appSettings?.apptShortName || "On-the-Fly Connections";
  const apptLongName  = appSettings?.apptLongName  || "Weekday Connections";
  const churchPhone   = appSettings?.churchPhone   || "";

  const [rows, setRows] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const data = await _apiGet("/admin/pastors-directory");
      setRows(Array.isArray(data) ? data : []);
      try {
        const u = await _apiGet("/users");
        setAllUsers(Array.isArray(u) ? u : []);
      } catch (e) { setAllUsers([]); }
    } catch (e) { setErr(e.message || "Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    setSavingId(id);
    try {
      await _apiPut(`/admin/pastors-directory/${id}`, body);
      setRows(prev => prev.map(r => r.id === id ? { ...r, ...body } : r));
    } catch (e) { alert(e.message || "Could not save"); }
    setSavingId(null);
  };

  const move = async (idx, delta) => {
    const next = idx + delta;
    if (next < 0 || next >= rows.length) return;
    const a = rows[idx]; const b = rows[next];
    const newA = { ...a, displayOrder: b.displayOrder };
    const newB = { ...b, displayOrder: a.displayOrder };
    const reordered = [...rows];
    reordered[idx] = newB; reordered[next] = newA;
    setRows(reordered);
    await Promise.all([
      _apiPut(`/admin/pastors-directory/${a.id}`, { displayOrder: newA.displayOrder }),
      _apiPut(`/admin/pastors-directory/${b.id}`, { displayOrder: newB.displayOrder }),
    ]).catch(e => alert(e.message || "Could not reorder"));
  };

  return (
    <div data-testid="pastors-directory-page" style={{ padding: "0 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <StructuralBackButton to="/dashboard" label="← Dashboard" testId="pastors-dir-back" />
        <div>
          <h1 style={{ fontFamily: "'Cinzel',serif", fontSize: 22, color: "var(--kc-text)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, margin: 0 }}>Pastors Directory</h1>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>Reorder, hide from public, edit bio &amp; specialty, set availability windows for {apptShortName} &amp; {apptLongName}, and toggle Away status.</div>
        </div>
      </div>

      {loading && <div data-testid="pastors-dir-loading" style={{ padding: 20, color: "var(--kc-sub)" }}>Loading…</div>}
      {err && <div style={{ padding: 12, background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.40)", color: "#ff6b6b", borderRadius: 8 }}>{err}</div>}

      {!loading && rows.length === 0 && !err && (
        <div className="card" data-testid="pastors-dir-empty" style={{ padding: 24, color: "var(--kc-sub)", textAlign: "center" }}>
          No users with role &quot;pastor&quot; yet. Promote a user to <strong>Pastor</strong> in Users &amp; Roles to populate this directory.
        </div>
      )}

      {!loading && rows.map((p, i) => (
        <PastorRow
          key={p.id}
          row={p} idx={i} total={rows.length}
          savingId={savingId}
          onMoveUp={() => move(i, -1)}
          onMoveDown={() => move(i, 1)}
          onPatch={(body) => patch(p.id, body)}
          churchPhoneDefault={churchPhone}
          apptShortName={apptShortName}
          apptLongName={apptLongName}
          allUsers={allUsers}
        />
      ))}
    </div>
  );
}

function PastorRow({ row: p, idx, total, savingId, onMoveUp, onMoveDown, onPatch, churchPhoneDefault, apptShortName, apptLongName, allUsers }) {
  const [shortAvail, setShortAvail] = useState(p.apptShortAvailability || { days: [], start: "", end: "" });
  const [longAvail,  setLongAvail]  = useState(p.apptLongAvailability  || { days: [], start: "", end: "" });
  const [isAway, setIsAway] = useState(!!p.isAway);
  const [awayMessage, setAwayMessage] = useState(p.awayMessage || `${p.name} is currently away. Please contact the main church line.`);
  const [awayPhone, setAwayPhone] = useState(p.awayPhone || churchPhoneDefault || "");
  const [assistantIds, setAssistantIds] = useState(Array.isArray(p.assistantIds) ? p.assistantIds : []);

  const toggleShortDay = (d) => {
    setShortAvail(s => ({ ...s, days: s.days.includes(d) ? s.days.filter(x => x !== d) : [...s.days, d] }));
  };
  const toggleLongDay = (d) => {
    setLongAvail(s => ({ ...s, days: s.days.includes(d) ? s.days.filter(x => x !== d) : [...s.days, d] }));
  };

  const saveAvailability = () => onPatch({
    apptShortAvailability: shortAvail,
    apptLongAvailability:  longAvail,
  });
  const saveAway = () => onPatch({
    isAway,
    awayMessage,
    awayPhone,
  });

  return (
    <div data-testid={`pastor-dir-row-${p.id}`} className="card" style={{ padding: 16, marginBottom: 12, opacity: p.hideFromPublic ? 0.55 : 1 }}>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", flexShrink: 0 }}>
          <button className="btn btn-sm btn-outline" data-testid={`pastor-dir-up-${p.id}`} disabled={idx === 0 || savingId === p.id} onClick={onMoveUp} title="Move up">↑</button>
          <span style={{ fontSize: 10, color: "var(--kc-meta)", fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>#{idx + 1}</span>
          <button className="btn btn-sm btn-outline" data-testid={`pastor-dir-down-${p.id}`} disabled={idx === total - 1 || savingId === p.id} onClick={onMoveDown} title="Move down">↓</button>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase" }}>{p.name}</div>
              <div style={{ fontSize: 11, color: "var(--kc-sub)" }}>@{p.username} · {p.email || "no email"}</div>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <label data-testid={`pastor-dir-hide-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: p.hideFromPublic ? "#ff6b6b" : "rgba(255,255,255,0.65)", fontFamily: "'Montserrat',sans-serif", fontWeight: 600, cursor: "pointer" }}>
                <input type="checkbox" checked={!!p.hideFromPublic} data-testid={`pastor-dir-hide-toggle-${p.id}`} disabled={savingId === p.id} onChange={e => onPatch({ hideFromPublic: e.target.checked })} />
                Hide from public
              </label>
              <label data-testid={`pastor-dir-away-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: isAway ? "var(--kc-accent)" : "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", fontWeight: 600, cursor: "pointer" }}>
                <input type="checkbox" checked={isAway} data-testid={`pastor-dir-away-toggle-${p.id}`} disabled={savingId === p.id} onChange={e => { setIsAway(e.target.checked); onPatch({ isAway: e.target.checked }); }} />
                Away
              </label>
            </div>
          </div>

          <div className="form-group mb-10">
            <label htmlFor={`bio-${p.id}`}>Public bio</label>
            <textarea id={`bio-${p.id}`} data-testid={`pastor-dir-bio-${p.id}`} rows={2} maxLength={500} defaultValue={p.publicBio} onBlur={e => e.target.value !== p.publicBio && onPatch({ publicBio: e.target.value })} placeholder="Short bio visible on the public booking page (max 500 chars)." />
          </div>
          <div className="form-group mb-12">
            <label htmlFor={`spec-${p.id}`}>Specialty / focus area</label>
            <input id={`spec-${p.id}`} type="text" data-testid={`pastor-dir-specialty-${p.id}`} maxLength={120} defaultValue={p.publicSpecialty} onBlur={e => e.target.value !== p.publicSpecialty && onPatch({ publicSpecialty: e.target.value })} placeholder="e.g. Pre-marriage counselling · Youth · Grief" />
          </div>

          {/* Short appointment availability */}
          <AvailabilityBlock
            id={p.id} kind="short" label={apptShortName} days={SHORT_DAYS}
            value={shortAvail}
            onToggleDay={toggleShortDay}
            onChangeStart={(v) => setShortAvail(s => ({ ...s, start: v }))}
            onChangeEnd={(v) => setShortAvail(s => ({ ...s, end: v }))}
          />
          {/* Long appointment availability */}
          <AvailabilityBlock
            id={p.id} kind="long" label={apptLongName} days={LONG_DAYS}
            value={longAvail}
            onToggleDay={toggleLongDay}
            onChangeStart={(v) => setLongAvail(s => ({ ...s, start: v }))}
            onChangeEnd={(v) => setLongAvail(s => ({ ...s, end: v }))}
          />
          <div style={{ marginTop: 6, marginBottom: 12 }}>
            <button className="btn btn-sm btn-gold" data-testid={`pastor-dir-save-avail-${p.id}`} disabled={savingId === p.id} onClick={saveAvailability}>
              Save availability
            </button>
          </div>

          {/* Assistants assigned to this pastor */}
          <AssistantsPicker
            pastorId={p.id}
            allUsers={allUsers}
            initial={assistantIds}
            onChange={setAssistantIds}
            onSave={() => onPatch({ assistantIds })}
            saving={savingId === p.id}
          />

          {/* Away notice */}
          {isAway && (
            <div className="form-group mb-10" data-testid={`pastor-dir-away-fields-${p.id}`} style={{ border: "1px dashed color-mix(in srgb, var(--kc-accent) 40%, transparent)", padding: 10, borderRadius: 8 }}>
              <div className="form-section-title mb-8" style={{ fontSize: 12 }}>Away notice</div>
              <label htmlFor={`away-msg-${p.id}`}>Custom away message</label>
              <textarea id={`away-msg-${p.id}`} data-testid={`pastor-dir-away-message-${p.id}`} rows={2} maxLength={400} value={awayMessage} onChange={e => setAwayMessage(e.target.value)} />
              <label htmlFor={`away-ph-${p.id}`} style={{ marginTop: 6 }}>Main church phone for this notice</label>
              <input id={`away-ph-${p.id}`} data-testid={`pastor-dir-away-phone-${p.id}`} maxLength={40} value={awayPhone} onChange={e => setAwayPhone(e.target.value)} placeholder="+233 ..." />
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-sm btn-gold" data-testid={`pastor-dir-save-away-${p.id}`} disabled={savingId === p.id} onClick={saveAway}>
                  Save away notice
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AvailabilityBlock({ id, kind, label, days, value, onToggleDay, onChangeStart, onChangeEnd }) {
  const sel = value && Array.isArray(value.days) ? value.days : [];
  return (
    <div className="form-group mb-10" data-testid={`pastor-dir-avail-${kind}-${id}`} style={{ background: "color-mix(in srgb, var(--kc-accent) 4%, transparent)", padding: 10, borderRadius: 8, border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)" }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{label} availability</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {days.map(d => {
          const selected = sel.includes(d);
          return (
            <button
              key={d}
              type="button"
              data-testid={`pastor-dir-avail-${kind}-${id}-day-${d}`}
              onClick={() => onToggleDay(d)}
              style={{
                fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
                padding: "5px 10px", borderRadius: 999,
                background: selected ? "var(--kc-accent)" : "transparent",
                color: selected ? "var(--kc-on-accent)" : "var(--kc-sub)",
                border: selected ? "1px solid var(--kc-accent)" : "1px solid rgba(255,255,255,0.20)",
                cursor: "pointer", letterSpacing: "0.08em",
              }}
            >{DAY_LABEL[d]}</button>
          );
        })}
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label>Start time</label>
          <input type="time" data-testid={`pastor-dir-avail-${kind}-${id}-start`} value={value?.start || ""} onChange={e => onChangeStart(e.target.value)} />
        </div>
        <div className="form-group">
          <label>End time</label>
          <input type="time" data-testid={`pastor-dir-avail-${kind}-${id}-end`} value={value?.end || ""} onChange={e => onChangeEnd(e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// AssistantsPicker — live-search filtered to team_member users, chip per
// assigned assistant with photo, removable. Save button persists.
function AssistantsPicker({ pastorId, allUsers, initial, onChange, onSave, saving }) {
  const [q, setQ] = useState("");
  const teamPool = (allUsers || []).filter(u => u.role === "team_member" && u.id !== pastorId);
  const assigned = (initial || []).map(id => (allUsers || []).find(u => u.id === id)).filter(Boolean);

  const ql = q.trim().toLowerCase();
  const suggestions = ql.length === 0 ? [] : teamPool
    .filter(u => !(initial || []).includes(u.id))
    .filter(u => (u.name || "").toLowerCase().includes(ql) || (u.username || "").toLowerCase().includes(ql))
    .slice(0, 8);

  const add = (id) => {
    if (!(initial || []).includes(id)) onChange([...(initial || []), id]);
    setQ("");
  };
  const remove = (id) => onChange((initial || []).filter(x => x !== id));

  return (
    <div className="form-group mb-10" data-testid={`pastor-dir-assistants-${pastorId}`} style={{ background: "rgba(255,255,255,0.03)", padding: 10, borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Assistants</div>
      <p style={{ fontSize: 11, color: "var(--kc-sub)", margin: "0 0 8px" }}>The pastor can reroute incoming requests to any of these team members from Administrative Tasks.</p>

      {/* Assigned chips with photo + remove */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8, minHeight: 30 }}>
        {assigned.length === 0 && <span style={{ fontSize: 11, color: "var(--kc-meta)" }}>No assistants assigned yet.</span>}
        {assigned.map(u => (
          <span key={u.id} data-testid={`pastor-dir-assistant-chip-${pastorId}-${u.id}`} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "3px 8px 3px 3px", borderRadius: 999,
            background: "var(--kc-accent)", color: "#1A1A1A", fontSize: 12, fontWeight: 700,
          }}>
            {u.photo
              ? <Avatar name={u.name || u.username} photo={u.photo} memberId={u.memberId} style={{ width: 22, height: 22, fontSize: 11 }} />
              : <Avatar name={u.name || u.username} memberId={u.memberId} style={{ width: 22, height: 22, background: "rgba(26,26,26,0.30)", color: "#1A1A1A", fontSize: 11 }} />}
            <span>{u.name || u.username}</span>
            <button
              type="button"
              data-testid={`pastor-dir-assistant-remove-${pastorId}-${u.id}`}
              onClick={() => remove(u.id)}
              aria-label={`Remove ${u.name}`}
              style={{ background: "rgba(26,26,26,0.30)", color: "#1A1A1A", border: "none", borderRadius: "50%", width: 18, height: 18, cursor: "pointer", fontSize: 14, lineHeight: 1, marginLeft: 2 }}
            >×</button>
          </span>
        ))}
      </div>

      {/* Live search */}
      <div style={{ position: "relative" }}>
        <input
          type="text"
          data-testid={`pastor-dir-assistant-search-${pastorId}`}
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search team members by name…"
          autoComplete="off"
          style={{ width: "100%" }}
        />
        {suggestions.length > 0 && (
          <div data-testid={`pastor-dir-assistant-suggestions-${pastorId}`} style={{
            position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)",
            background: "var(--kc-elevated)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 10,
            zIndex: 5, maxHeight: 220, overflowY: "auto", padding: 4,
          }}>
            {suggestions.map(u => (
              <button
                key={u.id}
                type="button"
                data-testid={`pastor-dir-assistant-suggestion-${pastorId}-${u.id}`}
                onClick={() => add(u.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", width: "100%", textAlign: "left", background: "transparent", border: "none", color: "var(--kc-text)", borderRadius: 6, cursor: "pointer", fontSize: 13 }}
              >
                {u.photo
                  ? <Avatar name={u.name || u.username} photo={u.photo} memberId={u.memberId} style={{ width: 24, height: 24, fontSize: 12 }} />
                  : <Avatar name={u.name || u.username} memberId={u.memberId} style={{ width: 24, height: 24, background: "color-mix(in srgb, var(--kc-accent) 20%, transparent)", color: "var(--kc-accent)", fontSize: 12, fontWeight: 700 }} />}
                <span style={{ flex: 1 }}>{u.name || u.username}</span>
                <span style={{ fontSize: 10, color: "var(--kc-meta)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Team</span>
              </button>
            ))}
          </div>
        )}
        {q.trim().length > 0 && suggestions.length === 0 && (
          <div data-testid={`pastor-dir-assistant-noresults-${pastorId}`} style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", background: "var(--kc-elevated)", border: "1px dashed color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, padding: 10, fontSize: 12, color: "var(--kc-sub)", zIndex: 5 }}>
            No team members match &quot;{q}&quot;.
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <button className="btn btn-sm btn-outline" data-testid={`pastor-dir-save-assistants-${pastorId}`} disabled={saving} onClick={onSave}>
          Save assistants
        </button>
      </div>
    </div>
  );
}

